/**
 * Sprint 29.5B finding #1 — primary-nav icon set.
 *
 * Custom 16×16 SVG glyphs (no external icon library; the design
 * system has no icon exports). Every icon uses `currentColor` so
 * it inherits the active / inactive pill colour from `<NavLink>`.
 *
 * The eight icons map 1-to-1 to the brief's nav slots:
 *   - DashboardIcon    → /dashboard
 *   - MapPinIcon       → /map (Live Map)
 *   - TruckIcon        → /vehicles
 *   - RouteIcon        → /trips
 *   - BellIcon         → /alerts
 *   - ShieldIcon       → /admin (role-gated)
 *   - MoreIcon         → "More ▾" popover
 *   - GroupsIcon, GeofenceIcon, DeviceIcon, DriverIcon,
 *     InsightsIcon, ReportsIcon, ImportsIcon, RulesIcon
 *     → secondary items rendered inside the More popover and the
 *       mobile drawer.
 *
 * Visual contract: 16×16 viewBox, 1.5 stroke-width, no fill
 * (`fill="none"`), `stroke="currentColor"` so the active pill (dark
 * text) and the inactive pill (muted text) both pick up the right
 * tone without per-state SVG variants. `aria-hidden` is set on the
 * `<svg>` because the accompanying `<span>` carries the label text;
 * screen readers read the label, not the icon.
 */

import type { CSSProperties } from "react";

const SVG_PROPS = {
  width: 16,
  height: 16,
  viewBox: "0 0 16 16",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.5,
  strokeLinecap: "round",
  strokeLinejoin: "round",
  "aria-hidden": true,
  style: { display: "block", flexShrink: 0 } as CSSProperties,
} as const;

export function DashboardIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="2" y="2" width="5" height="6" rx="1" />
      <rect x="9" y="2" width="5" height="3.5" rx="1" />
      <rect x="9" y="7" width="5" height="7" rx="1" />
      <rect x="2" y="9.5" width="5" height="4.5" rx="1" />
    </svg>
  );
}

export function MapPinIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M8 14.5c0 0 4.5-3.5 4.5-7.5a4.5 4.5 0 0 0-9 0c0 4 4.5 7.5 4.5 7.5z" />
      <circle cx="8" cy="7" r="1.5" />
    </svg>
  );
}

export function TruckIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="1.5" y="5" width="8" height="6" rx="0.5" />
      <path d="M9.5 7.5h3l2 2v1.5h-5z" />
      <circle cx="4" cy="12" r="1.2" />
      <circle cx="12" cy="12" r="1.2" />
    </svg>
  );
}

export function RouteIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="3" cy="3" r="1.5" />
      <circle cx="13" cy="13" r="1.5" />
      <path d="M3 4.5v3a2 2 0 0 0 2 2h3a3 3 0 0 1 3 3v.5" />
    </svg>
  );
}

export function BellIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3.5 11.5h9l-1-1.5V7a3.5 3.5 0 0 0-7 0v3z" />
      <path d="M6.5 13a1.5 1.5 0 0 0 3 0" />
    </svg>
  );
}

export function ShieldIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M8 1.5l5.5 2v4.5c0 3-2.5 5.5-5.5 6.5-3-1-5.5-3.5-5.5-6.5V3.5z" />
      <path d="M5.75 8l1.5 1.5L10.5 6.5" />
    </svg>
  );
}

export function MoreIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="3.5" cy="8" r="1" fill="currentColor" />
      <circle cx="8" cy="8" r="1" fill="currentColor" />
      <circle cx="12.5" cy="8" r="1" fill="currentColor" />
    </svg>
  );
}

export function GroupsIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="5.5" cy="6" r="2" />
      <circle cx="11" cy="5" r="1.5" />
      <path d="M2 13c.5-2 2-3 3.5-3s3 1 3.5 3" />
      <path d="M9.5 12c.5-1.5 1.5-2.5 3-2.5 1 0 1.5.4 1.5 1" />
    </svg>
  );
}

export function GeofenceIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3 3l10 1v8l-10 1z" />
      <path d="M3 3l5 5 5-4" />
    </svg>
  );
}

export function DeviceIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="4" y="1.5" width="8" height="13" rx="1.5" />
      <line x1="6" y1="12" x2="10" y2="12" />
    </svg>
  );
}

export function DriverIcon() {
  return (
    <svg {...SVG_PROPS}>
      <circle cx="8" cy="5.5" r="2.5" />
      <path d="M2.5 14c.5-3 3-4.5 5.5-4.5s5 1.5 5.5 4.5" />
    </svg>
  );
}

export function InsightsIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M2 13l3.5-4 3 2.5L13 5" />
      <circle cx="13" cy="5" r="1.2" />
    </svg>
  );
}

/**
 * GT-GFI-AI-COMMAND-CENTER-UX-FOUNDATION-01 — the AI Command Center
 * (`/intelligence`). A four-point sparkle + a small accent star reads as
 * "AI / intelligence" and stays distinct from the InsightsIcon line-chart.
 */
export function IntelligenceIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M8 2.5l1.4 3.1L12.5 7 9.4 8.4 8 11.5 6.6 8.4 3.5 7l3.1-1.4z" />
      <path d="M12.5 11.5l.5 1.1 1.1.5-1.1.5-.5 1.1-.5-1.1-1.1-.5 1.1-.5z" />
    </svg>
  );
}

export function ReportsIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3.5 1.5h7l3 3v10h-10z" />
      <path d="M10.5 1.5v3h3" />
      <line x1="5.5" y1="8" x2="11" y2="8" />
      <line x1="5.5" y1="11" x2="11" y2="11" />
    </svg>
  );
}

export function ImportsIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M8 2v8" />
      <path d="M5 7l3 3 3-3" />
      <path d="M2.5 12.5h11" />
    </svg>
  );
}

export function RulesIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3 3.5h4l2 2.5h5v6.5H3z" />
      <line x1="6" y1="9.5" x2="11" y2="9.5" />
    </svg>
  );
}

// Sprint 48.12 — fuel events nav icon for the More popover.
// Glyph: a stylised fuel pump.
export function FuelIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="3" y="3" width="6" height="11" rx="1" />
      <line x1="3" y1="6" x2="9" y2="6" />
      <path d="M9 7h2.5a1 1 0 0 1 1 1v3.5a1 1 0 0 0 1 1" />
      <line x1="13.5" y1="5" x2="13.5" y2="6.5" />
    </svg>
  );
}

// Sprint 47.0 — additional nav icons for the More popover.
export function MaintenanceIcon() {
  // Wrench glyph.
  return (
    <svg {...SVG_PROPS}>
      <path d="M11 3.5a3 3 0 1 1 1.5 5.2L7.2 14l-2 .5.5-2 5.3-5.3A3 3 0 0 1 11 3.5z" />
      <circle cx="11.5" cy="4.5" r="0.7" />
    </svg>
  );
}
export function CalendarIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="2.5" y="3.5" width="11" height="10" rx="1" />
      <line x1="2.5" y1="6.5" x2="13.5" y2="6.5" />
      <line x1="5" y1="2" x2="5" y2="5" />
      <line x1="11" y1="2" x2="11" y2="5" />
    </svg>
  );
}
export function SupportIcon() {
  // Life-ring / chat-bubble glyph.
  return (
    <svg {...SVG_PROPS}>
      <path d="M3 8.5C3 5.5 5.5 3 8.5 3S14 5.5 14 8.5c0 1.4-.5 2.6-1.4 3.6L13 14l-2-.7a5.5 5.5 0 0 1-2.5.7C5.5 14 3 11.5 3 8.5z" />
      <circle cx="8.5" cy="8.5" r="1" />
    </svg>
  );
}
export function TelegramIcon() {
  // Paper-plane glyph.
  return (
    <svg {...SVG_PROPS}>
      <path d="M14 2 2 7l4 1.5L13 4l-5 6 5 4 1-12z" />
    </svg>
  );
}
export function RbacIcon() {
  // Person + shield glyph (compact).
  return (
    <svg {...SVG_PROPS}>
      <circle cx="6" cy="5.5" r="2" />
      <path d="M2.5 13c.5-2 2-3 3.5-3s3 1 3.5 3" />
      <path d="M11 4l3 1v3c0 2-1.5 3-3 3.5-1.5-.5-3-1.5-3-3.5V5z" />
    </svg>
  );
}

// GT-APP-SHELL-2-NAVIGATION-AND-FLEET-PULSE-01 — Archived-items glyph (a box
// with a lid + a pull slot) for the Admin group's Lifecycle / Archived entry.
export function ArchiveIcon() {
  return (
    <svg {...SVG_PROPS}>
      <rect x="2.5" y="3" width="11" height="3" rx="0.5" />
      <path d="M3.5 6v6.5h9V6" />
      <line x1="6.5" y1="9" x2="9.5" y2="9" />
    </svg>
  );
}

// GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 — Automation glyph (a
// lightning bolt + a small spark) for the Genius Automations product-family entry.
// Reads as "AI-assisted automation" and stays distinct from the AI sparkle.
export function AutomationIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M8.5 1.5 3.5 8.5H7l-.5 6 5-7H8z" />
      <path d="M12.5 2.5l.4.9.9.4-.9.4-.4.9-.4-.9-.9-.4.9-.4z" />
    </svg>
  );
}

// GT-NAV-IA-DOMAIN-GROUPING-FUEL-COSTS-GENIUS-FAMILY-01 — Integration/channel glyph
// (a plug) for the Integrations group's connector entries (WhatsApp / Email / Webhooks
// / API). A generic, honest "connect a channel" affordance.
export function PlugIcon() {
  return (
    <svg {...SVG_PROPS}>
      <line x1="6" y1="1.5" x2="6" y2="4" />
      <line x1="10" y1="1.5" x2="10" y2="4" />
      <path d="M4.5 4h7v2.5a3.5 3.5 0 0 1-7 0z" />
      <path d="M8 10v2a2 2 0 0 1-2 2H4" />
    </svg>
  );
}

// GT-APP-SHELL-2-NAVIGATION-AND-FLEET-PULSE-01 — External-link glyph (a box with
// an out-arrow) for the Platform Admin Console out-link, which opens the separate
// admin SPA in a new tab. The honest affordance for "leaves this app".
export function ExternalLinkIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M7 3.5H4a1.5 1.5 0 0 0-1.5 1.5v6A1.5 1.5 0 0 0 4 12.5h6A1.5 1.5 0 0 0 11.5 11V8" />
      <path d="M9.5 2.5H13v3.5" />
      <line x1="13" y1="2.5" x2="7.5" y2="8" />
    </svg>
  );
}

// Paket 003707 — small glyphs for the App Store / Google Play footer badges in the
// icon rail (collapsed sidebar) and the mobile drawer tooltip. The full badge
// graphics (expanded sidebar) are local SVG assets under `public/brand/`; these
// glyphs are the compact fallback when there is no room for the full badge.
export function AppStoreIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M10.5 2.5c-.4.5-1 .9-1.6.8-.1-.6.2-1.3.6-1.7.4-.5 1-.8 1.6-.8.1.6-.2 1.2-.6 1.7z" />
      <path d="M11 6.2c-.9-.5-1.9-.5-2.4-.5-.9 0-1.7.5-2.2.5-.5 0-1.2-.5-2-.5-1 0-2.5.9-2.5 3 0 2.5 1.8 5.3 2.7 5.3.5 0 .8-.4 1.6-.4.8 0 1 .4 1.6.4.9 0 2.6-2.5 2.6-5 0-.3 0-1.4-.4-1.8-.6-.4-1-.6-1-1z" />
    </svg>
  );
}
export function GooglePlayIcon() {
  return (
    <svg {...SVG_PROPS}>
      <path d="M3 2.2v11.6c0 .3.3.5.6.4l8.8-5.3c.5-.3.5-1 0-1.3L3.6 1.8c-.3-.2-.6 0-.6.4z" />
    </svg>
  );
}

// A-MODUL-NAV1 — Schloss-Glyphe fuer die Kennzeichnung "nur lesen" im Menue
// (Modul in der 90-Tage-Lesefrist). 12x12 statt 16x16, weil das Symbol in der
// Pille neben dem Wort sitzt und nicht wie ein Menuesymbol wirken darf.
export function LockIcon() {
  return (
    <svg {...SVG_PROPS} width={12} height={12}>
      <rect x="3.5" y="7" width="9" height="6.5" rx="1.25" />
      <path d="M5.75 7V5a2.25 2.25 0 0 1 4.5 0v2" />
    </svg>
  );
}
